<template>
    <section id="tab-receive-mail" class='receive-mail'>
        <!-- Recipient -->
        <AutocompleteSuggestCustomers
            id="inputMailRecipient"
            ref="mailRecipient"
            label="Recipient *"
            :rules="[ hasRecipient ]"
            :value="models.recipient"
            @input="( option ) => setRecipient( option )"
        />

        <!-- Sender -->
        <AutocompleteSuggestSender
            id="inputMailSender"
            ref="mailSender"
            label="Sender"
            allowCreate
            :value="models.sender"
            @input="( sender ) => setSender( sender )"
        />

        <!-- Quantity -->
        <ClInput
            id="inputMailQuantity"
            ref="mailQuantity"
            v-model="models.quantity"
            label="Quantity"
            type="number"
            min="1"
            @keydown.enter.prevent="focusAddButton()"
        />

        <!-- Transaction Notes -->
        <v-textarea
            id="inputMailNotes"
            ref="mailNotes"
            v-model="models.transactionNotes"
            outlined
            dense
            hideDetails
            label="Transaction Notes"
            type="text"
        />

        <!-- Button Group -->
        <div class="receive-mail__actions">
            <div v-if="useDynamicMailbox" class="available-folders">
                <span class="detail-label">Available Folders:</span>
                <div :class="getAvailableFolderColor">
                    <MaterialDesignIcon
                        :className="getAvailableFolderIcon"
                        pxSize="18"
                    />
                    <span :class="getAvailableFolderColor">{{ availableFolders }}</span>
                </div>
            </div>
            <div v-else></div>
            <ClButton
                ref="buttonAdd"
                :class="{ 'is-loading': receiveInFlight }"
                :disabled="receiveInFlight || !hasRequiredFields"
                @click="nextButtonHandler()"
            >
                Add
            </ClButton>
        </div>
    </section>
</template>

<script>
// Vuex
import { mapGetters, mapState } from 'vuex';

// Api
import LogisticsApi from '@/api/LogisticsApi';

// Utils
import DateUtil from '@/utils/DateUtil';

// Components
import AutocompleteSuggestCustomers from '@/components/AutocompleteSuggestCustomers';
import AutocompleteSuggestSender from '@/components/AutocompleteSuggestSender';
import MaterialDesignIcon from '@/components/base/MaterialDesignIcon';

// Defaults
const defaults = {
    models: () => ( {
        recipient: null,
        sender: null,
        quantity: '1',
        transactionNotes: null,
    } ),
};

// Typemap
const typeMap = {
    FULL: {
        css: 'is-full',
        icon: 'skull-crossbones-outline',
        minFolders: 0,
    },
    CRITICAL: {
        css: 'is-critical',
        icon: 'circle-slice-7',
        minFolders: 0,
    },
    WARNING: {
        css: 'is-warning',
        icon: 'circle-slice-6',
        minFolders: 10,
    },
    CONCERN: {
        css: 'is-concerning',
        icon: 'circle-slice-5',
        minFolders: 20,
    },
    GOOD: {
        css: 'is-good',
        icon: 'thumb-up',
        minFolders: 30,
    },
};

export default {
    name: 'TabMail',
    components: {
        MaterialDesignIcon,
        AutocompleteSuggestSender,
        AutocompleteSuggestCustomers,
    },
    props: {
        receiveDate: {
            type: Date,
            required: false,
            default: DateUtil.now(),
        },
        carrier: {
            type: Object,
            required: true,
        },
        receiveInFlight: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            models: defaults.models(),
            availableFolders: 0,
            useDynamicMailbox: false,
            inFlight: false,
        };
    },
    computed: {
        ...mapGetters( {
            websiteId: 'Website/id',
        } ),
        ...mapState( {
            jwtToken: ( state ) => state.User.jwtToken,
            userAccountId: ( state ) => state.User.accountId,
            loggedInLocation: ( state ) => state.User.selectedLocation,
        } ),
        /** @returns { Boolean } */
        hasRecipient() {
            return !!this.models.recipient
                && ( Object.values( this.models.recipient ).length > 0 );
        },
        /** @returns { Boolean } */
        hasCarrier() {
            return ( Object.values( this.carrier ).length > 0 );
        },
        /** @returns { Boolean } */
        hasRequiredFields() {
            return this.hasCarrier && this.hasRecipient;
        },
        getAvailableFolderColor() {
            const mapName = this.selectAvailableFolderLevel();
            return typeMap[ mapName ].css;
        },
        getAvailableFolderIcon() {
            const mapName = this.selectAvailableFolderLevel();
            return typeMap[ mapName ].icon;
        },
    },
    watch: {
        availableFolders( val ) {
            this.availableFolders = val;
        },
    },
    mounted() {
        this.$emit( 'defaultMailCarrier' );
        this.getDynamicMailboxInfo();
    },
    methods: {
        initialFocus() {
            this.$refs.mailRecipient.$refs.autocomplete.focus();
        },
        focusAddButton() {
            if ( this.hasRequiredFields ) {
                this.$refs.buttonAdd.$el.focus();
            }
        },
        inFlightToggle() {
            this.inFlight = !this.inFlight;
        },
        setRecipient( option ) {
            this.models.recipient = option || {};
            if ( this.hasRecipient ) {
                this.$refs.mailSender.$refs.autocomplete.focus();
            }
        },

        setSender( sender ) {
            this.models.sender = sender;

            // We don't have a ref for the internal input, since it is from the CL
            // so we dig into the js object to set the focus
            this.$refs.mailQuantity.$el.querySelector( 'input' ).focus();
        },

        nextField( refName ) {
            this.$refs[ refName ].focus();
        },

        selectAvailableFolderLevel() {
            let mapName;
            if ( this.availableFolders > typeMap.GOOD.minFolders ) {
                mapName = 'GOOD';
            } else if ( this.availableFolders > typeMap.CONCERN.minFolders ) {
                mapName = 'CONCERN';
            } else if ( this.availableFolders > typeMap.WARNING.minFolders ) {
                mapName = 'WARNING';
            } else if ( this.availableFolders > typeMap.CRITICAL.minFolders ) {
                mapName = 'CRITICAL';
            } else {
                mapName = 'FULL';
            }
            return mapName;
        },

        /** @returns { Boolean } */
        canProceedToNextPackage() {
            if ( this.hasRequiredFields ) return true;
            this.$store.dispatch( 'snackbar', {
                message: 'Select a Carrier and fill out the Recipient',
                type: 'error',
                duration: 5000,
            }, { root: true } );
            return false;
        },
        async addNewMail() {
            this.$emit( 'newMail', {
                date: this.receiveDate,
                carrier: this.carrier,
                recipient: this.models.recipient,
                sender: this.models.sender,
                quantity: this.models.quantity,
                notes: this.models.transactionNotes,
            } );
            if ( this.useDynamicMailbox ) {
                await this.getDynamicMailboxInfo();
            }
        },
        nextButtonHandler() {
            if ( this.canProceedToNextPackage() ) {
                this.addNewMail();
                this.models = defaults.models();
            }
        },
        async getDynamicMailboxInfo() {
            this.inFlightToggle();
            try {
                const { data } = await LogisticsApi.getDynamicMailboxStatus( {
                    jwtToken: this.jwtToken,
                    websiteId: this.websiteId,
                    userAccountId: this.userAccountId,
                } );
                this.availableFolders = data.availableFolders;
                this.useDynamicMailbox = true;
            } catch ( error ) {
                this.useDynamicMailbox = false;
                const errorMessage = error
                    && error.response.data.message.toLowerCase().includes( 'no dynamic' )
                    ? `Dynamic Mail is not set up for ${ this.loggedInLocation.nameFriendly }. Enable Dynamic Mail in 'Settings'`
                    : error.response.data.message;
                await this.$store.dispatch( 'snackbar', {
                    message: errorMessage,
                    type: 'error',
                    duration: 5000,
                }, { root: true } );
            } finally {
                this.inFlightToggle();
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.receive-mail {
    display: grid;
    grid-template-columns: auto 2.5rem;
    row-gap: 1rem;

    & > * {
        grid-column: 1 / 1;
    }
    &__actions {
        display: flex;
        justify-content: space-between;
    }
}

.available-folders {
    display: flex;
    flex-direction: row;
    justify-items: flex-start;

    .detail-label {
        font-size: 0.8rem;
        padding-top: 0.3rem;
        padding-right: 0.4rem;
    }
}

.is-full {
    background-color: black;
    color: white;
    padding: 0.1rem 0.4rem 0.1rem 0.6rem;
}
.is-critical {
    background-color: red;
    color: white;
    padding: 0.1rem 0.4rem 0.1rem 0.6rem;
}
.is-warning {
    background-color: darkorange;
    color: black;
    padding: 0.1rem 0.4rem 0.1rem 0.6rem;
}
.is-concerning {
    background-color: lightblue;
    color: black;
    padding: 0.1rem 0.4rem 0.1rem 0.6rem;
}
.is-good {
    color: black;
    padding: 0.1rem 0.4rem 0.1rem 0.6rem;
}
</style>
